import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, Chip, CircularProgress, LinearProgress, MenuItem, Select, Typography } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { userApiSlice } from '../../../features/user/userSlice'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import CustomInput from '../../../components/muiComponents/customInput'
import StudentsTable from './components/StudentsTable'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import { ui } from '../../../utils/uiTokens'
import { capitalize } from '../../../utils/functions'
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants'
import { useInfiniteScroll } from '../../../app/hooks/useInfiniteScroll'
import { CuratorStudentState } from '../../../features/types'

const styles = {
    boxContainer: {
        margin: '10px',
        borderRadius: '16px',
    },
    filterBox: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    },
    search: {
        width: '440px',
    },
    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        mb: 2.5,
    },
    filterGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
    },
    groupSelect: {
        minWidth: 190,
        borderRadius: ui.radius,
        fontSize: '13.5px',
        fontWeight: 600,
        color: ui.ink,
        backgroundColor: ui.card,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
    },
}

const StudentListPage = () => {
    const [searchParams, setSearchParams] = useSearchParams('')
    const { formatMessage, locale } = useIntl()
    const [filter, setFilter] = useState('')
    const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(
        Number(searchParams.get('courseId')) || undefined,
    )
    const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(
        Number(searchParams.get('groupId')) || undefined,
    )
    const [monthNumber, setMonthNumber] = useState<number | undefined>(
        Number(searchParams.get('monthNumber')) || undefined,
    )
    const [weekNumber, setWeekNumber] = useState<number | undefined>(
        Number(searchParams.get('weekNumber')) || undefined,
    )
    const [search, setSearch] = useState('')
    const [rows, setRows] = useState<CuratorStudentState[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [loadedPages, setLoadedPages] = useState(0)
    // Ответы приходят не по порядку: применяем только последний запрос
    const requestIdRef = useRef(0)
    const isLoadingRef = useRef(false)

    const [fetchStudents, { isFetching }] =
        curatorStudentsApiSlice.useLazyFetchAllStudentsQuery()
    const [fetchCourses, { data: courseResponse = null }] =
        curatorStudentsApiSlice.useLazyFetchCuratorCoursesQuery()
    const { data: groups = [] } = curatorStudentsApiSlice.useFetchCuratorGroupsQuery(
        selectedCourseId,
        { skip: !selectedCourseId },
    )
    const [resetTemporaryPassword, { isLoading: isReseting }] =
        userApiSlice.useResetTemporaryPasswordMutation()

    const hasMore = rows.length < totalCount

    const loadPage = async (nextPage: number) => {
        if (!selectedCourseId) return
        if (nextPage > 1 && isLoadingRef.current) return

        const requestId = ++requestIdRef.current
        isLoadingRef.current = true

        try {
            const response = await fetchStudents({
                filter: search,
                page: nextPage - 1,
                size: DEFAULT_PAGE_SIZE,
                courseId: selectedCourseId,
                groupId: selectedGroupId,
                monthNumber,
                weekNumber,
            }).unwrap()

            if (requestId !== requestIdRef.current) return

            setRows((prev) => (nextPage === 1 ? response.data : [...prev, ...response.data]))
            // Короткая страница означает конец списка: расхождение с totalCount
            // иначе гоняло бы наблюдатель по пустым страницам без остановки
            setTotalCount(response.data.length < DEFAULT_PAGE_SIZE
                ? (nextPage - 1) * DEFAULT_PAGE_SIZE + response.data.length
                : response.totalCount)
            setLoadedPages(nextPage)
        } catch (error) {
            // Без остановки наблюдатель повторял бы упавший запрос бесконечно
            if (requestId === requestIdRef.current) setTotalCount((nextPage - 1) * DEFAULT_PAGE_SIZE)
        } finally {
            if (requestId === requestIdRef.current) isLoadingRef.current = false
        }
    }

    const sentinelRef = useInfiniteScroll(
        () => loadPage(loadedPages + 1),
        hasMore && !isFetching && loadedPages > 0,
    )

    const handleResetPassword = async (id: number) => {
        try {
            await resetTemporaryPassword(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            loadPage(1)
        } catch (error) { }
    }

    const handleCourseClick = (courseId: number) => {
        setSelectedCourseId(courseId)
        setSelectedGroupId(undefined)
    }

    // Проценты считаются по темам выбранного периода
    const handlePeriodChange = (month?: number, week?: number) => {
        setMonthNumber(month)
        setWeekNumber(week)
    }

    useEffect(() => {
        fetchCourses()
    }, [])

    // Курс и группа живут в URL, иначе после перезагрузки список молча
    // возвращается к первому курсу
    useEffect(() => {
        const params: Record<string, string> = {}
        if (selectedCourseId) params.courseId = selectedCourseId.toString()
        if (selectedGroupId) params.groupId = selectedGroupId.toString()
        if (monthNumber) params.monthNumber = monthNumber.toString()
        if (weekNumber) params.weekNumber = weekNumber.toString()
        setSearchParams(params, { replace: true })

        setRows([])
        setTotalCount(0)
        setLoadedPages(0)
        loadPage(1)
    }, [selectedCourseId, selectedGroupId, monthNumber, weekNumber, search])

    useEffect(() => {
        if (courseResponse?.data?.length > 0 && !selectedCourseId) {
            setSelectedCourseId(courseResponse.data[0].id)
        }
    }, [courseResponse])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'students' })}</H3>
                    </Title>
                </Header>

                <Box sx={styles.filterBox}>
                    <Box sx={styles.searchBox}>
                        <CustomInput
                            sx={styles.search}
                            value={filter}
                            margin="none"
                            type="text"
                            placeholder={formatMessage({
                                id: 'searchByStudentName',
                            })}
                            size="small"
                            onChange={(e) => setFilter(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSearch(filter)
                                }
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={() => setSearch(filter)}
                        >
                            {formatMessage({ id: 'search' })}
                        </Button>
                    </Box>
                </Box>
                <ChipGroup>
                        {courseResponse?.data?.map((course) => (
                            <Chip
                                label={course[`title${capitalize(locale)}`]}
                                key={course.id}
                                onClick={() => handleCourseClick(course.id)}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: selectedCourseId === course.id ? 600 : 400,
                                    background: selectedCourseId === course.id ? ui.pinkSoft : 'transparent',
                                    border: `1px solid ${selectedCourseId === course.id ? 'transparent' : ui.border}`,
                                    color: selectedCourseId === course.id ? ui.pink : ui.sub,
                                    '&:hover': { background: ui.pinkSoft, color: ui.pink },
                                }}
                            />
                        ))}
                </ChipGroup>

                <Box sx={styles.filterRow}>
                    <Box sx={styles.filterGroup}>
                        <Select
                            size="small"
                            displayEmpty
                            value={selectedGroupId ?? ''}
                            onChange={(e) => setSelectedGroupId(Number(e.target.value) || undefined)}
                            sx={styles.groupSelect}
                        >
                            <MenuItem value="">{formatMessage({ id: 'allGroups' })}</MenuItem>
                            {groups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.title}
                                </MenuItem>
                            ))}
                        </Select>

                        <MonthWeekFilter
                            monthNumber={monthNumber}
                            weekNumber={weekNumber}
                            onChange={handlePeriodChange}
                        />
                    </Box>

                    <Typography variant="body1" sx={{ color: ui.sub }}>
                        {formatMessage({ id: 'all' })} {' : '}
                        <span style={{ fontWeight: 600, color: ui.pink }}>{totalCount} {formatMessage({ id: 'student' })}</span>
                    </Typography>
                </Box>
                {(isFetching && loadedPages === 0) || isReseting ? (
                    <LinearProgress />
                ) : (
                    <>
                        <StudentsTable
                            studentsData={rows}
                            isFetching={isFetching}
                            onResetPassword={handleResetPassword}
                        />

                        <Sentinel ref={sentinelRef}>
                            {isFetching && <CircularProgress size={22} sx={{ color: ui.pink }} />}
                        </Sentinel>
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

export default StudentListPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
    color: #17141A;
`

const Content = styled.div`
    background-color: #fff;
    border: 1px solid #F0E8EE;
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
`

const Sentinel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
`

const ChipGroup = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin: 20px 0 16px;
`